/**
 * LiveKit → Agora track adapters (CORE-393, web).
 *
 * The live-broadcast surfaces (AgoraViewer / MasterLivePublisher / GuestRoomScreen)
 * store remote/local tracks in refs and render them imperatively with the Agora API
 * `track.play(container)` / `track.setVolume(0..100)`. To reuse all of that layout,
 * PiP, cohost-tile and volume logic unchanged for LiveKit, we wrap a LiveKit `Track`
 * in a tiny object exposing the same methods. `__lk` marks it as an adapter.
 */
import type { Track } from 'livekit-client';

export interface AgoraLikeVideoTrack {
  __lk: true;
  /** Attach the video into `el` (idempotent — reuses one <video> per container). */
  play: (el: HTMLElement) => void;
  stop: () => void;
}

export interface AgoraLikeAudioTrack {
  __lk: true;
  /** Start audio playback (LiveKit auto-plays on attach). */
  play: () => void;
  /** Agora passes 0..100; LiveKit wants 0..1. */
  setVolume: (v: number) => void;
  stop: () => void;
}

export function wrapVideoTrack(track: Track): AgoraLikeVideoTrack {
  return {
    __lk: true,
    play: (el: HTMLElement) => {
      let v = el.querySelector('video') as HTMLVideoElement | null;
      if (!v) {
        v = document.createElement('video');
        v.autoplay = true;
        v.playsInline = true;
        (v as any).muted = false;
        v.style.width = '100%';
        v.style.height = '100%';
        v.style.objectFit = 'cover';
        el.replaceChildren(v);
      }
      track.attach(v);
    },
    stop: () => {
      try {
        track.detach();
      } catch {
        /* ignore */
      }
    },
  };
}

export function wrapAudioTrack(track: Track): AgoraLikeAudioTrack {
  let el: HTMLMediaElement | null = null;
  return {
    __lk: true,
    play: () => {
      if (!el) el = track.attach();
    },
    setVolume: (v: number) => {
      const rt = track as unknown as { setVolume?: (n: number) => void };
      if (typeof rt.setVolume === 'function') {
        rt.setVolume(Math.max(0, Math.min(1, v / 100)));
      }
    },
    stop: () => {
      try {
        track.detach();
      } catch {
        /* ignore */
      }
      el = null;
    },
  };
}
